import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { normalizeChoices, reorderChoices } from '../utils/helpers';
import styles from './fields.module.css';
import { IoCheckmarkDoneSharp, IoChevronDownSharp } from 'react-icons/io5';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import { Icon } from '../../Icon';

export interface TagboxFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const TagboxField: React.FC<TagboxFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const error = errors[element.name];

  const normalizedChoices = normalizeChoices(element.choices);
  const choices = reorderChoices(normalizedChoices, element.choicesOrder);

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue || []}
        render={({ field }) => {
          const selectedValues: string[] = Array.isArray(field.value)
            ? field.value
            : [];

          const filteredChoices = choices.filter((choice) =>
            choice.text.toLowerCase().includes(searchTerm.toLowerCase())
          );

          const toggleOption = (value: string) => {
            if (selectedValues.includes(value)) {
              field.onChange(selectedValues.filter((v) => v !== value));
            } else {
              field.onChange([...selectedValues, value]);
            }
          };

          const removeTag = (value: string) => {
            field.onChange(selectedValues.filter((v) => v !== value));
          };

          const getChoiceText = (value: string): string => {
            const choice = choices.find((c) => c.value === value);
            return choice ? choice.text : value;
          };

          return (
            <div style={{ position: 'relative' }}>
              <div
                className={`${styles.input} ${styles.selectContainer}`}
                role="button"
                tabIndex={0}
                style={{
                  cursor: element.readOnly ? 'not-allowed' : 'pointer',
                }}
                onClick={() => !element.readOnly && setIsOpen(!isOpen)}
                onKeyDown={(e) => {
                  if (
                    (e.key === 'Enter' || e.key === ' ') &&
                    !element.readOnly
                  ) {
                    e.preventDefault();
                    setIsOpen(!isOpen);
                  }
                }}
              >
                {selectedValues.map((value) => (
                  <span
                    key={value}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.5rem',
                      backgroundColor: '#5166B233',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#311E86',
                      fontWeight: 600,
                    }}
                  >
                    {getChoiceText(value)}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(value);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '12px',
                        color: '#311E86',
                        fontWeight: 600,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                {selectedValues.length === 0 && (
                  <span style={{ color: '#999' }}>
                    {element.placeholder || 'Selecione opções'}
                  </span>
                )}
                <Icon
                  icon={IoChevronDownSharp}
                  className={`${styles.chevronDownIconSelect} ${
                    isOpen ? styles.open : ''
                  }`}
                  size={15}
                />
              </div>

              {isOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 100,
                  }}
                >
                  <div
                    style={{
                      padding: '16px 12px',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <div className={styles.searchContainer}>
                      <Icon
                        icon={HiMagnifyingGlass}
                        className={styles.searchIcon}
                        size={20}
                      />
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSearchTerm(e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {filteredChoices.map((choice, index) => {
                    const isSelected = selectedValues.includes(choice.value);

                    return (
                      <div
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleOption(choice.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleOption(choice.value);
                          }
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                          backgroundColor: isSelected
                            ? 'var(--df-primary-color-light, rgba(20, 57, 113, 0.1))'
                            : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <button
                          type="button"
                          className={`${styles.checkBoxBtn} ${
                            isSelected
                              ? styles.checkBoxBtnSelected
                              : styles.checkBoxBtnUnselected
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOption(choice.value);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {isSelected && (
                            <Icon
                              icon={IoCheckmarkDoneSharp}
                              className={`${styles.checkBoxIcon}`}
                              size={18}
                            />
                          )}
                        </button>
                        {choice.text}
                      </div>
                    );
                  })}

                  {filteredChoices.length === 0 && (
                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        color: '#999',
                        textAlign: 'center',
                      }}
                    >
                      Nenhuma opção encontrada
                    </div>
                  )}
                </div>
              )}

              {isOpen && (
                <div
                  role="button"
                  tabIndex={-1}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 99,
                  }}
                  onClick={() => setIsOpen(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsOpen(false);
                    }
                  }}
                  aria-label="Fechar menu"
                />
              )}
            </div>
          );
        }}
      />
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default TagboxField;
